import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';

import { IAuthenticationData, IAuthenticationInfo } from '../../../models/models-index';
import { Enums, IEnums } from '../../../enums/enums';
import { AppInsightsService } from '../../appInsights/appInsights.service';
import { AuthenticationService } from '../authentication.service';
import { environment } from '../../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthenticationInterceptorService implements HttpInterceptor {
  private enums: IEnums = Enums;
  private user: IAuthenticationInfo;
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private appInsights: AppInsightsService) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.includes('/auth/refresh')) {
      return next.handle(request);
    }

    return next.handle(this.applyCredentials(request)).pipe(
      catchError((err: any) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          return this.handleUnauthorized(request, next, err);
        }

        this.appInsights.trackException(err);
        return throwError(() => err);
      })
    );
  }

  private handleUnauthorized(request: HttpRequest<any>, next: HttpHandler, err: HttpErrorResponse): Observable<HttpEvent<any>> {
    const refreshToken = this.authService.getRefreshToken();
    if (!refreshToken) {
      this.redirectToLogin(err);
      return throwError(() => err);
    }

    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.applyAccessToken(request, token!)))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.authService.refreshCurrentSession().pipe(
      switchMap(user => {
        this.refreshTokenSubject.next(user.token);
        return next.handle(this.applyAccessToken(request, user.token));
      }),
      catchError(refreshError => {
        this.appInsights.trackException(refreshError);
        this.redirectToLogin(refreshError);
        return throwError(() => refreshError);
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }

  private applyCredentials(request: HttpRequest<any>): HttpRequest<any> {
    this.user = this.authService.getUser();

    if (!this.user) {
      return request;
    }

    const headers = request.body instanceof FormData
      ? this.setHttpHeaders(this.user, request.headers, false)
      : this.setHttpHeaders(this.user, request.headers);

    return request.clone({ headers });
  }

  private applyAccessToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    let headers = request.headers ?? new HttpHeaders();
    headers = headers.set('X-Auth-Token', token);
    headers = headers.set('Authorization', 'Bearer ' + token);

    if (!(request.body instanceof FormData) && !headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/json');
    }

    if (!headers.has('Accept')) {
      headers = headers.set('Accept', 'application/json');
    }

    return request.clone({ headers });
  }

  private setHttpHeaders(user: IAuthenticationInfo, existingHeaders: HttpHeaders = null, setContentType = true): HttpHeaders {
    let myHeaders: HttpHeaders;
    try {
      if (existingHeaders && existingHeaders.keys) {
        existingHeaders.keys();
        myHeaders = existingHeaders;
      } else {
        myHeaders = new HttpHeaders();
      }
    } catch (error) {
      console.warn('Invalid HttpHeaders state detected, creating new headers instance', error);
      myHeaders = new HttpHeaders();
    }

    const safeSetHeader = (key: string, value: string): void => {
      try {
        if (!myHeaders.has(key)) {
          myHeaders = myHeaders.set(key, value);
        }
      } catch (error) {
        console.warn(`Error checking header ${key}, forcing set`, error);
        myHeaders = myHeaders.set(key, value);
      }
    };

    safeSetHeader('Access-Control-Allow-Origin', '*');
    safeSetHeader('Access-Control-Allow-Credentials', 'true');
    safeSetHeader('Accept', 'application/json');
    safeSetHeader('X-Auth-Token', user.token);
    safeSetHeader('Authorization', 'Bearer ' + user.token);

    if (setContentType) {
      safeSetHeader('Content-Type', 'application/json');
    }

    return myHeaders;
  }

  private redirectToLogin(error: any): void {
    this.authService.resetUser();
    console.error('Http Error', error);

    if (!!environment.loginRedirectUrl) {
      window.location.href = environment.loginRedirectUrl;
      return;
    }

    this.router.navigateByUrl('/login');
  }

  private setDownloadHttpHeaders(authData: IAuthenticationData): HttpHeaders {
    const myHeaders = new HttpHeaders()
      .set('Content-Type', 'text/csv')
      .set('Accept', 'text/csv')
      .set('X-Auth-Token', authData.token)
      .set('Authorization', 'Bearer ' + authData.token);

    return myHeaders;
  }

  private setUploadHttpHeaders(authData: IAuthenticationData): HttpHeaders {
    const myHeaders = new HttpHeaders()
      .set('X-Auth-Token', authData.token)
      .set('Authorization', 'Bearer ' + authData.token);

    return myHeaders;
  }

  private trackException(exception: any): void {
    // this.ai.trackException(exception);
  }
}
